import type { FC } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCustomerGet } from '../../../../bff-api/customers/useCustomerGet';
import { useMoveAllSaveForLaterToBasket } from '../../../../bff-api/customers/useMoveAllSaveForLaterToBasket';
import { useSaveForLater } from '../../../../bff-api/customers/useSaveForLater';
import { isGuest } from '../../../../contexts/utils/customer-utils';
import { useSLTToast } from '../../../../hooks/use-toast/useSLTToast';
import { Button, Header } from '../../../../ui-components';
import { Carousel } from '../../../../ui-components/carousel/Carousel';
import styles from '../../../../ui-components/carousel/Carousel.module.scss';
import { classNames } from '../../../../utils/tailwind/classNames';
import { SaveForLaterSkeleton } from '../CartSkeleton';
import { SaveForLaterProduct } from './SaveForLaterProduct';
import type { Product } from '../../../../bff-api/products/model';

export const SaveForLaterWidget: FC = () => {
    const intl = useIntl();
    const showToast = useSLTToast();
    const { data, isLoading } = useSaveForLater();
    const moveAllSaveForLaterToBasket = useMoveAllSaveForLaterToBasket();
    const { data: customer } = useCustomerGet();

    if (!customer || isGuest(customer) || (!isLoading && !data?.customerProductListItems?.length)) {
        return null;
    }

    const onAddAllToBag = async (): Promise<void> => {
        await moveAllSaveForLaterToBasket.mutateAsync(null);

        showToast(
            intl.formatMessage({
                id: 'multiple.items.has.been.added.to.bag',
                defaultMessage: 'Multiple Items has been added to bag',
            }),
        );
    };

    const products = data?.customerProductListItems?.map((item) => item.c_product!) ?? [];

    return (
        <SaveForLaterSkeleton isLoaded={!isLoading}>
            <Flex
                data-testid="save-for-later"
                direction="column"
                backgroundColor="white"
                width="100%"
                alignItems="center"
                boxShadow="0px 2px 10px 0px rgba(65, 64, 66, 0.14)"
                borderRadius={{ md: 16 }}
            >
                <Flex
                    width="100%"
                    justifyContent="space-between"
                    borderBottomColor="slt-pepper.400"
                    borderBottomWidth={1}
                    padding={{
                        base: '1rem',
                        sm: '1rem 2rem',
                        md: '1rem',
                    }}
                    rowGap={2}
                    direction={{ base: 'column', sm: 'row' }}
                >
                    <Header
                        id="save-for-later-products-title"
                        as="h4"
                        variant="header4"
                        style="bold"
                    >
                        <FormattedMessage
                            id="save.for.later.x"
                            defaultMessage="Save For Later ({count})"
                            values={{ count: products.length }}
                        />
                    </Header>

                    <Button
                        id="save-for-later-products-add-all-to-bag-button"
                        variant="secondary"
                        onClick={onAddAllToBag}
                        isLoading={moveAllSaveForLaterToBasket.isLoading}
                    >
                        <FormattedMessage id="add.all.to.bag" defaultMessage="Add all to bag" />
                    </Button>
                </Flex>

                <Carousel
                    id="save-for-later-widget"
                    wrapperClassName={classNames(
                        styles.saveForLaterWidget,
                        'xs:px-4 xs:pt-8 xs:pb-4 sm:py-8 sm:px-8 md:p-4',
                    )}
                    products={products}
                    renderProduct={(product: Product) => (
                        <SaveForLaterProduct
                            key={product.id}
                            product={product}
                            isDisabled={moveAllSaveForLaterToBasket.isLoading}
                        />
                    )}
                />
            </Flex>
        </SaveForLaterSkeleton>
    );
};
